import React, {useState, useEffect} from 'react';
import {
    Box,
    Typography,
    TextField,
    FormControl,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormGroup,
    Checkbox,
    Card,
    CardContent,
} from '@mui/material';
import Quantino from '../../../components/Quantino/Quantino';

const motivationalMessages = [
    "You're doing amazing!",
    "Keep that energy up!",
    "Every step counts!",
    "I believe in you!",
    "You've got this!",
    "Stay focused!",
    "Great progress so far!",
    "Don't give up now!",
    "You're building something great!",
    "Learning looks good on you!",
    "Your effort is paying off!",
    "Knowledge is power - keep going!"
];

const contextualMessages = {
    'text': [
        "Time to show your knowledge!",
        "Express your thoughts clearly.",
        "Let your creativity flow!",
        "Think it through step by step.",
        "Your words have power here!",
        "Tell me what you think!",
        "Knowledge sharing time!"
    ],
    'single_choice': [
        "Choose wisely!",
        "One perfect answer awaits.",
        "Trust your instincts!",
        "Take your time to decide.",
        "Which one speaks to you?",
        "Your gut knows the answer!",
        "Decision time - you've got this!"
    ],
    'multi_choice': [
        "Multiple treasures to find!",
        "Don't miss any correct ones!",
        "Check all that apply!",
        "Think broadly here.",
        "Collect all the right answers!",
        "Multiple paths to success!",
        "Hunt for all the correct ones!"
    ],
    'number': [
        "Numbers don't lie!",
        "Precision is key here.",
        "Calculate carefully!",
        "Math time!",
        "Let's crunch some numbers!",
        "Time for some calculation magic!",
        "Show me those math skills!"
    ],
    'string': [
        "Keep it concise!",
        "Short and sweet works best.",
        "Get to the point!",
        "Quality over quantity.",
        "Brief but brilliant!",
        "Clarity is king here!",
        "Make every word count!"
    ]
};

const encouragementMessages = [
    "You're on fire!",
    "Brilliant thinking!",
    "That's the spirit!",
    "Keep it up!",
    "Excellent work!",
    "You're crushing it!",
    "Way to go!",
    "Outstanding!",
    "Fantastic job!",
    "You're unstoppable!",
    "Impressive progress!",
    "You're really getting this!",
    "Wonderful effort!",
    "Keep that momentum!"
];

const thinkingMessages = [
    "Hmm, interesting question...",
    "Let me think about this one.",
    "This looks challenging!",
    "Take your time with this.",
    "What's your gut feeling?",
    "Consider all angles.",
    "Think step by step.",
    "You know this!",
    "What does your instinct say?",
    "Take a moment to reflect.",
    "This one's worth pondering!",
    "Think it through carefully."
];

const idleMoods = ['neutral', 'nachdenklich', 'motivierend', 'freundlich', 'zwinkernd', 'schlafend', 'schwindelig'];
const encouragingMoods = ['motivierend', 'freundlich', 'feiernd', 'stolz_daumen_hoch', 'überrascht'];
const thinkingMoods = ['nachdenklich', 'neutral', 'überrascht', 'streng', 'traurig'];

const QuestionRenderer = ({question, answer, onAnswerChange, questionNumber}) => {
    const [quantinoState, setQuantinoState] = useState({
        mood: 'neutral',
        message: '',
        isBubbleVisible: false
    });
    const [numberInputError, setNumberInputError] = useState(false);

    // Reset error state when question changes
    useEffect(() => {
        setNumberInputError(false);
    }, [question.id]);

    useEffect(() => {
        let bubbleTimeout;

        // Show contextual message for first question only
        if (questionNumber === 1 && contextualMessages[question.type]) {
            const contextMessages = contextualMessages[question.type];
            const randomContextMessage = contextMessages[Math.floor(Math.random() * contextMessages.length)];

            setQuantinoState({
                mood: 'motivierend',
                message: randomContextMessage,
                isBubbleVisible: true,
            });

            bubbleTimeout = setTimeout(() => {
                setQuantinoState(prevState => ({...prevState, isBubbleVisible: false}));
            }, 8000);

            // Random encouragement/thinking messages (20% chance, roughly every 3-5 questions)
        } else if (questionNumber > 1 && Math.random() < 0.2) {
            const isEncouragement = Math.random() < 0.6; // 60% encouragement, 40% thinking
            const messages = isEncouragement ? encouragementMessages : thinkingMessages;
            const moods = isEncouragement ? encouragingMoods : thinkingMoods;

            const randomMessage = messages[Math.floor(Math.random() * messages.length)];
            const randomMood = moods[Math.floor(Math.random() * moods.length)];

            setQuantinoState({
                mood: randomMood,
                message: randomMessage,
                isBubbleVisible: true,
            });

            bubbleTimeout = setTimeout(() => {
                setQuantinoState(prevState => ({...prevState, isBubbleVisible: false}));
            }, 7000);

            // Random motivational message on every 8th question (milestone check-ins)
        } else if (questionNumber > 1 && questionNumber % 8 === 0) {
            const randomMessage = motivationalMessages[Math.floor(Math.random() * motivationalMessages.length)];
            const randomMood = encouragingMoods[Math.floor(Math.random() * encouragingMoods.length)];

            setQuantinoState({
                mood: randomMood,
                message: randomMessage,
                isBubbleVisible: true,
            });

            bubbleTimeout = setTimeout(() => {
                setQuantinoState(prevState => ({...prevState, isBubbleVisible: false}));
            }, 7000);

        } else {
            // Just random mood, no message - silent for most questions
            const randomMood = idleMoods[Math.floor(Math.random() * idleMoods.length)];
            setQuantinoState({
                mood: randomMood,
                message: '',
                isBubbleVisible: false,
            });
        }

        return () => {
            clearTimeout(bubbleTimeout);
        };
    }, [question.id, question.type, questionNumber]);

    const handleInputChange = (value) => {
        onAnswerChange(question.id, value);
    };

    const renderQuestionInput = () => {
        switch (question.type) {
            case 'text':
                return (
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={answer || ''}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder="Gib hier deine Antwort ein..."
                        variant="outlined"
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.100',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}40`,
                                }
                            }
                        }}
                    />
                );

            case 'string':
                return (
                    <TextField
                        fullWidth
                        value={answer || ''}
                        onChange={(e) => handleInputChange(e.target.value)}
                        placeholder="Gib hier deine Antwort ein..."
                        variant="outlined"
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.100',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}40`,
                                }
                            }
                        }}
                    />
                );

            case 'number':
                return (
                    <TextField
                        fullWidth
                        type="number"
                        value={answer ?? ''}
                        onChange={(e) => {
                            const inputElement = e.target;
                            const value = e.target.value;

                            // Check for bad input (invalid number format like "e12")
                            // validity.badInput is true when text can't be converted to a number
                            if (inputElement.validity.badInput) {
                                setNumberInputError(true);
                                // Don't update the answer since it's invalid
                            } else {
                                setNumberInputError(false);
                                handleInputChange(value);
                            }
                        }}
                        onBlur={(e) => {
                            // Validate on blur as well
                            const inputElement = e.target;
                            if (inputElement.validity.badInput) {
                                setNumberInputError(true);
                            }
                        }}
                        error={numberInputError}
                        helperText={numberInputError ? "Bitte gib eine gültige Zahl ein (z.B. 42 oder 1e12)" : ""}
                        placeholder="Gib eine Zahl ein..."
                        variant="outlined"
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50',
                                '&:hover': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'grey.100',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'white',
                                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}40`,
                                }
                            }
                        }}
                    />
                );

            case 'single_choice':
                return (
                    <Box>
                        {/* Subtle hint text */}
                        <Typography variant="caption" sx={{
                            color: 'text.secondary',
                            mb: 2,
                            display: 'block',
                            fontStyle: 'italic'
                        }}>
                            Select one answer
                        </Typography>

                        <FormControl component="fieldset" sx={{width: '100%'}}>
                            <RadioGroup
                                value={answer || ''}
                                onChange={(e) => handleInputChange(e.target.value)}
                            >
                                {question.options?.map((option) => (
                                    <FormControlLabel
                                        key={option.id}
                                        value={option.value}
                                        control={<Radio sx={{color: 'primary.main'}}/>}
                                        label={
                                            <Typography variant="body1" sx={{fontWeight: 'medium'}}>
                                                {option.label}
                                            </Typography>
                                        }
                                        sx={{
                                            mb: 1,
                                            p: 2,
                                            borderRadius: 1,
                                            border: 2,
                                            borderColor: (theme) => answer === option.value ? 'primary.main' :
                                                (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'grey.300'),
                                            backgroundColor: (theme) => answer === option.value
                                                ? (theme.palette.mode === 'dark'
                                                    ? 'rgba(130, 177, 255, 0.15)'
                                                    : 'rgba(41, 98, 255, 0.08)')
                                                : (theme.palette.mode === 'dark'
                                                    ? 'rgba(255,255,255,0.05)'
                                                    : 'grey.50'),
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: (theme) => answer === option.value
                                                    ? (theme.palette.mode === 'dark'
                                                        ? 'rgba(130, 177, 255, 0.2)'
                                                        : 'rgba(41, 98, 255, 0.12)')
                                                    : (theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.08)'
                                                        : 'grey.100'),
                                                transform: 'translateY(-1px)',
                                                boxShadow: (theme) => theme.palette.mode === 'dark'
                                                    ? '0 2px 8px rgba(0,0,0,0.3)'
                                                    : '0 2px 8px rgba(0,0,0,0.1)',
                                            },
                                        }}
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>
                    </Box>
                );

            case 'multi_choice':
                return (
                    <Box>
                        {/* Subtle hint text */}
                        <Typography variant="caption" sx={{
                            color: 'text.secondary',
                            mb: 2,
                            display: 'block',
                            fontStyle: 'italic'
                        }}>
                            Select all correct answers
                        </Typography>

                        <FormControl component="fieldset" sx={{width: '100%'}}>
                            <FormGroup>
                                {question.options?.map((option) => {
                                    const selectedValues = Array.isArray(answer) ? answer : [];
                                    const isChecked = selectedValues.includes(option.value);

                                    return (
                                        <FormControlLabel
                                            key={option.id}
                                            control={
                                                <Checkbox
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        const newValues = isChecked
                                                            ? selectedValues.filter(v => v !== option.value)
                                                            : [...selectedValues, option.value];
                                                        handleInputChange(newValues);
                                                    }}
                                                    sx={{color: 'primary.main'}}
                                                />
                                            }
                                            label={
                                                <Typography variant="body1" sx={{fontWeight: 'medium'}}>
                                                    {option.label}
                                                </Typography>
                                            }
                                            sx={{
                                                mb: 1,
                                                p: 2,
                                                borderRadius: 2,
                                                border: 2,
                                                borderColor: (theme) => isChecked ? 'primary.main' :
                                                    (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'grey.300'),
                                                backgroundColor: (theme) => isChecked
                                                    ? (theme.palette.mode === 'dark'
                                                        ? 'rgba(130, 177, 255, 0.15)'
                                                        : 'rgba(41, 98, 255, 0.08)')
                                                    : (theme.palette.mode === 'dark'
                                                        ? 'rgba(255,255,255,0.05)'
                                                        : 'grey.50'),
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    backgroundColor: (theme) => isChecked
                                                        ? (theme.palette.mode === 'dark'
                                                            ? 'rgba(130, 177, 255, 0.2)'
                                                            : 'rgba(41, 98, 255, 0.12)')
                                                        : (theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.08)'
                                                            : 'grey.100'),
                                                    transform: 'translateY(-1px)',
                                                    boxShadow: (theme) => theme.palette.mode === 'dark'
                                                        ? '0 2px 8px rgba(0,0,0,0.3)'
                                                        : '0 2px 8px rgba(0,0,0,0.1)',
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </FormGroup>
                        </FormControl>
                    </Box>
                );

            default:
                return (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 2}}>
                        Question type "{question.type}" is not supported yet.
                    </Typography>
                );
        }
    };

    return (
        <Box sx={{display: 'flex', gap: 0, alignItems: 'flex-start'}}>
            {/* Quantino auf der linken Seite */}
            <Box sx={{
                flexShrink: 0,
                position: 'sticky',
                top: 20,
                zIndex: 10
            }}>
                <Quantino
                    mood={quantinoState.mood}
                    message={quantinoState.message}
                    isVisible={true}
                    isBubbleVisible={quantinoState.isBubbleVisible}
                />
            </Box>

            {/* Single Modern Card with Question & Answer */}
            <Box sx={{flex: 1}}>
                <Card sx={{
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    background: (theme) =>
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(145deg, #22262F 0%, #2A2F3A 100%)'
                            : 'linear-gradient(145deg, #ffffff 0%, #f8fafe 100%)',
                    width: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 12px 48px rgba(0,0,0,0.16)',
                        transform: 'translateY(-2px)',
                    }
                }}>
                    {/* Question Header */}
                    <Box sx={{
                        p: 4,
                        pb: 2,
                        //background: 'linear-gradient(135deg, #f8fafe 0%, #ffffff 100%)',
                        borderBottom: '1px solid',
                        borderColor: 'grey.100'
                    }}>
                        <Typography variant="h5" sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: '1.3rem',
                            lineHeight: 1.4,
                            mb: 0
                        }}>
                            {question.text}
                        </Typography>
                    </Box>

                    {/* Answer Section */}
                    <CardContent sx={{p: 4, pt: 3}}>
                        {renderQuestionInput()}
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};

export default QuestionRenderer;
